/**
 * CoordinationTab — the super-admin drawer's "Coordination" tab content.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3 AC4.
 *
 * A REORGANIZATION of CoordinationViewPage's 8-column full-width board for
 * the narrower drawer — NOT a raw transplant (that would be the exact "not
 * well organized" experience this ring exists to fix). Columns collapse to
 * the essentials (title + visibility, and the linked-feedback count) with
 * the internal planning detail (stage / priority / horizon / tenants +
 * visibility toggle) revealed on expand — the SAME expand-on-click idiom the
 * retired page used for linked feedback, now also covering the planning
 * fields the old 8-column table exposed as separate columns. Untriaged
 * feedback stays a CLEARLY SEPARATE section above the work-items list (never
 * merged into one table), per AC4's "clear split" requirement. Reuses the
 * shared `data-table.tsx` + `StatusPill` (AC9).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { StatusPill } from '@/components/portal/StatusPill';
import { SignedScreenshot } from '@/components/feedback/SignedScreenshot';
import { ImageGallery } from '@/components/feedback/ImageGallery';
import { PanelErrorState } from '@/components/portal/panel/PanelErrorState';
import { portalWriteError } from '@/lib/portal/mutationError';
import { MarkShippedDialog } from '@/components/portal/panel/MarkShippedDialog';
import { canMarkShipped, isRemark } from '@/lib/portal/markShipped';
import type {
  CoordinationWorkItem,
  CoordinationUntriagedFeedback,
  CoordinationLinkedFeedback,
} from '@/lib/schemas';

export const PANEL_COORDINATION_QUERY_KEY = ['stakeholder-portal', 'panel', 'coordination'] as const;

export function isPortalEligible(item: { visibility: string | null }): boolean {
  return item.visibility === 'stakeholder';
}

export function CoordinationTab() {
  const queryClient = useQueryClient();
  const [expandedWorkItemId, setExpandedWorkItemId] = useState<string | null>(null);
  // R4 (SPEC-feedback-return-path-2026-08-17): the "Ask a question" dialog is
  // keyed by the feedback row being asked about (null = closed).
  const [askingFor, setAskingFor] = useState<{ id: string; existing: string | null } | null>(null);
  const [questionText, setQuestionText] = useState('');
  // v0.17.16: the untriaged report waiting on a "Dismiss" confirmation.
  const [dismissing, setDismissing] = useState<CoordinationUntriagedFeedback | null>(null);

  const boardQuery = useQuery({
    queryKey: PANEL_COORDINATION_QUERY_KEY,
    queryFn: () => api.coordination.board(),
  });

  const askQuestionMutation = useMutation({
    mutationFn: (vars: { feedbackId: string; question: string }) =>
      api.coordination.askQuestion(vars.feedbackId, vars.question),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY });
      toast.success('Question sent.');
      setAskingFor(null);
      setQuestionText('');
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not send the question')),
  });

  const visibilityMutation = useMutation({
    mutationFn: (vars: { workItemId: string; visibility: string }) =>
      api.coordination.setPlanning(vars.workItemId, { visibility: vars.visibility }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY });
      toast.success('Visibility updated.');
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not update visibility')),
  });

  const promoteMutation = useMutation({
    mutationFn: (feedbackId: string) => api.coordination.promote(feedbackId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY });
      // A promote can half-succeed: the work item is committed to Keystone even
      // when the GitHub push fails. This used to claim both were created
      // regardless, which is how a completely unwired bridge went unnoticed for
      // the life of the feature. Report what actually happened.
      if (result?.github_issue_number) {
        toast.success(`Promoted. Work item + GitHub issue #${result.github_issue_number} created.`);
      } else {
        // CH-03 (v0.17 platform review): the OLD wording told the operator to
        // "re-run Promote" (returns 409 for an already-linked feedback) or to
        // wait for a "repair endpoint" that already existed on the backend
        // with no frontend action wired to it -- both recovery paths were
        // impossible from this toast. The repair-link endpoint IS the
        // authorized recovery action; wire it directly into the toast.
        const workItemId = result?.work_item_id;
        toast.warning(
          `Promoted to a work item (${workItemId ?? 'unknown id'}), but NO ` +
            'GitHub issue was created. ' +
            (result?.github_error ?? 'The GitHub bridge did not respond.') +
            ' This does not fix itself -- use Repair link to retry the GitHub bridge.',
          workItemId
            ? { action: { label: 'Repair link', onClick: () => repairLinkMutation.mutate(workItemId) } }
            : undefined
        );
      }
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not promote feedback')),
  });

  // v0.17.16: take an untriaged report off the board without promoting it.
  // No work item, no GitHub issue, no email to anyone.
  const dismissMutation = useMutation({
    mutationFn: (feedbackId: string) => api.coordination.dismiss(feedbackId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY });
      toast.success('Report dismissed. No one was emailed.');
      setDismissing(null);
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not dismiss the report')),
  });

  // CH-03: the authorized repair action for an orphaned (promoted-but-not-
  // bridged) work_item -- reuses the SAME backend endpoint the retired
  // 8-column CoordinationViewPage never wired either (routers/
  // coordination_view.py::repair_link_endpoint). Safe to call even when the
  // work_item already has an issue (no-op, already_bridged: true).
  const repairLinkMutation = useMutation({
    mutationFn: (workItemId: string) => api.coordination.repairLink(workItemId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY });
      if (result?.github_issue_number) {
        toast.success(`Repaired. GitHub issue #${result.github_issue_number} linked.`);
      } else {
        toast.error(
          'The repair attempt did not link a GitHub issue. ' +
            (result?.github_error ?? 'The GitHub bridge did not respond.')
        );
      }
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not repair the GitHub link')),
  });

  if (boardQuery.isError) {
    return (
      <PanelErrorState
        what="the coordination board"
        onRetry={() => boardQuery.refetch()}
        testId="panel-coordination-error"
      />
    );
  }

  if (boardQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 px-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the coordination board…
      </div>
    );
  }

  const workItems: CoordinationWorkItem[] = boardQuery.data?.work_items ?? [];
  const untriaged: CoordinationUntriagedFeedback[] = boardQuery.data?.untriaged_feedback ?? [];

  const columns: DataTableColumn<CoordinationWorkItem>[] = [
    {
      key: 'title',
      label: 'Work item',
      wrap: true,
      maxWidth: 'max-w-[220px]',
      render: (w) => (
        <div className="space-y-1">
          <span className="text-sm font-medium">
            {w.title || <span className="text-muted-foreground">(untitled)</span>}
            {w.github_issue_number != null && (
              <span className="ml-2 text-xs text-muted-foreground">#{w.github_issue_number}</span>
            )}
          </span>
          <span
            data-testid="coord-visibility"
            className={
              isPortalEligible(w)
                ? 'block w-fit rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800'
                : 'block w-fit rounded-full bg-muted px-2 py-0.5 text-xs'
            }
          >
            {w.visibility}
          </span>
        </div>
      ),
    },
    {
      key: 'expand',
      label: '',
      align: 'right',
      render: (w) => {
        const isExpanded = expandedWorkItemId === w.id;
        return (
          <button
            type="button"
            data-testid="coord-panel-expand-toggle"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-expanded={isExpanded}
            onClick={() => setExpandedWorkItemId((cur) => (cur === w.id ? null : w.id))}
          >
            <span data-testid="coord-feedback-count">{w.feedback_count}</span>
            <span>reports</span>
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        );
      },
    },
  ];

  return (
    <div className="p-3 space-y-4" data-testid="panel-coordination-tab">
      {/* AC4: a CLEARLY SEPARATE section — untriaged feedback is never merged
          into the work-items list below. */}
      <section data-testid="panel-coord-untriaged-section">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Untriaged ({untriaged.length})
        </h3>
        {untriaged.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="coord-untriaged-empty">
            Nothing waiting to be triaged.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="coord-untriaged-list">
            {untriaged.map((f) => (
              <li
                key={f.id}
                className="flex items-start justify-between gap-2 rounded-md border p-2"
                data-testid="coord-untriaged-item"
              >
                <div className="min-w-0">
                  <div className="text-xs whitespace-pre-wrap">{f.body}</div>
                  {/* Cross-ring gap close: render ALL of the report's attached
                      images (R5 shipped up to 5), not just screenshot_url.
                      legacyUrl is a pure back-compat fallback (pre-R5 reports). */}
                  <ImageGallery
                    images={f.images}
                    legacyUrl={f.screenshot_url}
                    alt="Reporter screenshot"
                    testId="coord-untriaged-screenshot"
                    linkClassName="mt-1 block w-fit"
                    imgClassName="max-h-16 rounded border object-cover"
                  />
                  <div className="text-[11px] text-muted-foreground">
                    {f.tenant_id ?? 'unknown tenant'}
                    {f.reporter_name ? ` · ${f.reporter_name}` : ''}
                  </div>
                  <QuestionAnswerBlock item={f} />
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Button
                    size="sm"
                    disabled={promoteMutation.isPending}
                    data-testid="coord-promote"
                    onClick={() => promoteMutation.mutate(f.id)}
                  >
                    Promote
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="coord-ask-question"
                    onClick={() => {
                      setAskingFor({ id: f.id, existing: f.operator_question ?? null });
                      setQuestionText(f.operator_question ?? '');
                    }}
                  >
                    {f.operator_question ? 'Edit question' : 'Ask a question'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="coord-dismiss"
                    onClick={() => setDismissing(f)}
                  >
                    Dismiss
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={dismissing !== null} onOpenChange={(open) => !open && setDismissing(null)}>
        <DialogContent data-testid="coord-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Dismiss this report?</DialogTitle>
            <DialogDescription>
              It leaves the untriaged list. No work item or GitHub issue is created, and no
              one is emailed, including the person who sent it.
            </DialogDescription>
          </DialogHeader>
          {dismissing && (
            <div className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border p-2 text-xs">
              {dismissing.body}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissing(null)}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              disabled={dismissMutation.isPending || dismissing === null}
              data-testid="coord-dismiss-confirm"
              onClick={() => dismissing && dismissMutation.mutate(dismissing.id)}
            >
              Dismiss report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AC4: the work-items list, reorganized -- essentials-only columns,
          the internal planning detail (stage/priority/horizon/tenants + the
          visibility toggle) revealed on expand instead of five extra
          columns. Deliberately NOT the retired page's 8-column table. */}
      <section data-testid="panel-coord-workitems-section">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Work items ({workItems.length})
        </h3>
        <DataTable<CoordinationWorkItem>
          columns={columns}
          rows={workItems}
          rowKey={(w) => w.id}
          containerTestId="panel-coordination-workitems-table"
          emptyState={<span className="text-muted-foreground">No work items yet.</span>}
        />
        {expandedWorkItemId && (
          <WorkItemDetail
            workItem={workItems.find((w) => w.id === expandedWorkItemId) ?? null}
            onToggleVisibility={(w) =>
              visibilityMutation.mutate({
                workItemId: w.id,
                visibility: isPortalEligible(w) ? 'internal' : 'stakeholder',
              })
            }
            togglePending={visibilityMutation.isPending}
            onClose={() => setExpandedWorkItemId(null)}
            onAskQuestion={(f) => {
              setAskingFor({ id: f.id, existing: f.operator_question ?? null });
              setQuestionText(f.operator_question ?? '');
            }}
            onShipped={() => queryClient.invalidateQueries({ queryKey: PANEL_COORDINATION_QUERY_KEY })}
          />
        )}
      </section>

      {/* R4 (SPEC-feedback-return-path-2026-08-17): ask/edit a question on a
          report -- works on an UNTRIAGED report (AC1/AC2) and on an
          already-triaged one reached via the linked-feedback list (AC12). */}
      <Dialog open={askingFor !== null} onOpenChange={(open) => !open && setAskingFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{askingFor?.existing ? 'Edit your question' : 'Ask a question'}</DialogTitle>
            <DialogDescription>
              This does not promote the report or set an outcome -- it just asks. The
              reporter is notified and can answer from where their reports already appear.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              data-testid="coord-question-text"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              rows={4}
              placeholder="What do you need to know before triaging this?"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAskingFor(null)}>
              Cancel
            </Button>
            <Button
              data-testid="coord-question-submit"
              disabled={questionText.trim().length === 0 || askQuestionMutation.isPending}
              onClick={() =>
                askingFor &&
                askQuestionMutation.mutate({ feedbackId: askingFor.id, question: questionText.trim() })
              }
            >
              {askQuestionMutation.isPending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** R4: renders an existing question/answer on a feedback row (untriaged or
 *  linked), or nothing when no question has been asked. Shared by the
 *  untriaged list and the linked-feedback detail so both surfaces render the
 *  SAME shape. */
function QuestionAnswerBlock({
  item,
}: {
  item: { operator_question?: string | null; reporter_answer?: string | null };
}) {
  if (!item.operator_question) return null;
  return (
    <div className="mt-1 space-y-0.5 rounded border bg-muted/40 p-1.5" data-testid="coord-question-answer">
      <div className="text-[11px]">
        <span className="font-medium text-muted-foreground">Q: </span>
        <span data-testid="coord-question-text-display">{item.operator_question}</span>
      </div>
      {item.reporter_answer ? (
        <div className="text-[11px]">
          <span className="font-medium text-muted-foreground">A: </span>
          <span data-testid="coord-answer-text-display">{item.reporter_answer}</span>
        </div>
      ) : (
        <div className="text-[11px] italic text-muted-foreground" data-testid="coord-answer-pending">
          Awaiting an answer.
        </div>
      )}
    </div>
  );
}

/** AC4's "detail on expand": the planning fields the old table showed as
 *  five columns (stage/priority/horizon/tenants + the visibility toggle),
 *  plus the linked-feedback detail the retired page already fetched on
 *  expand -- combined into one panel instead of two separate mechanisms. */
function WorkItemDetail({
  workItem,
  onToggleVisibility,
  togglePending,
  onClose,
  onAskQuestion,
  onShipped,
}: {
  workItem: CoordinationWorkItem | null;
  onToggleVisibility: (w: CoordinationWorkItem) => void;
  togglePending: boolean;
  onClose: () => void;
  onAskQuestion: (f: CoordinationLinkedFeedback) => void;
  onShipped: () => void | Promise<void>;
}) {
  const [shipOpen, setShipOpen] = useState(false);
  const feedbackQuery = useQuery({
    queryKey: ['coordination', 'panel', 'work-item-feedback', workItem?.id],
    queryFn: () => api.coordination.workItemFeedback(workItem!.id),
    enabled: !!workItem,
  });

  if (!workItem) return null;
  const rows: CoordinationLinkedFeedback[] = feedbackQuery.data?.feedback ?? [];
  // v0.17.16: after a reporter says "still broken", the item can be marked
  // fixed again once a new fix is out.
  const remark = isRemark(workItem, rows);

  return (
    <div className="mt-3 rounded-md border bg-muted/30 p-3 space-y-3" data-testid="coord-work-item-feedback-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {workItem.title || <span className="text-muted-foreground">(untitled)</span>}
        </h4>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="coord-feedback-close">
          Close
        </Button>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="coord-panel-planning-detail">
        <dt className="text-muted-foreground">Stage</dt>
        <dd>{workItem.stage ?? '—'}</dd>
        <dt className="text-muted-foreground">Priority</dt>
        <dd>{workItem.priority ?? '—'}</dd>
        <dt className="text-muted-foreground">Horizon</dt>
        <dd>{workItem.horizon ?? '—'}</dd>
        <dt className="text-muted-foreground">Tenants</dt>
        <dd data-testid="coord-tenants">{workItem.tenants.length > 0 ? workItem.tenants.join(', ') : '—'}</dd>
      </dl>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={togglePending}
          data-testid="coord-toggle-visibility"
          onClick={() => onToggleVisibility(workItem)}
        >
          {isPortalEligible(workItem) ? 'Make internal' : 'Make visible'}
        </Button>
        {/* v0.17.15: for a fix the bridge cannot see. Only on an open item
            that does not already read as shipped. v0.17.16: also offered
            again once a reporter says it is still broken. */}
        {canMarkShipped(workItem, rows) && (
          <Button
            size="sm"
            variant="outline"
            data-testid="coord-mark-shipped"
            onClick={() => setShipOpen(true)}
          >
            {remark ? 'Mark as fixed again' : 'Mark as fixed'}
          </Button>
        )}
      </div>
      <MarkShippedDialog
        workItemId={workItem.id}
        open={shipOpen}
        onOpenChange={setShipOpen}
        onDone={async () => {
          await onShipped();
          // v0.17.16: the linked reports carry still_broken_after_ship, which
          // a (re-)mark clears; refresh them so the chip and button update.
          await feedbackQuery.refetch();
        }}
      />

      <div>
        <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Linked feedback
        </h5>
        {feedbackQuery.isLoading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading reports…
          </div>
        ) : feedbackQuery.isError ? (
          <div className="py-2 text-xs text-destructive" data-testid="coord-feedback-error">
            Could not load the linked feedback.
          </div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="coord-feedback-empty">
            No linked feedback.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="coord-linked-feedback-list">
            {rows.map((f) => (
              <li key={f.id} className="rounded-md border bg-background p-2" data-testid="coord-linked-feedback-item">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {f.tenant_id ?? 'unknown tenant'}
                    {f.reporter_name ? ` · ${f.reporter_name}` : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    {/* v0.17.15: the reporter said "still broken" and has not
                        since confirmed it. v0.17.16: the server says whether
                        that came after the newest ship move, so the chip
                        clears once the item is marked fixed again. */}
                    {(f.still_broken_after_ship ??
                      (f.body?.includes('[Still broken') && f.acceptance_state !== 'accepted')) && (
                      <span
                        className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"
                        data-testid="coord-linked-feedback-still-broken"
                      >
                        Says still broken
                      </span>
                    )}
                    {f.acceptance_state && (
                      <StatusPill
                        rung={{ key: f.acceptance_state, label: f.acceptance_state }}
                        testId="coord-linked-feedback-state"
                      />
                    )}
                  </span>
                </div>
                {f.body && (
                  <div className="whitespace-pre-wrap text-xs" data-testid="coord-linked-feedback-body">
                    {f.body}
                  </div>
                )}
                {f.notes.length > 0 && (
                  <ul className="mt-1 space-y-0.5" data-testid="coord-linked-feedback-notes">
                    {f.notes.map((n, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground">
                        You: {n.text}
                      </li>
                    ))}
                  </ul>
                )}
                {/* The "still broken" regression evidence keeps priority (a
                    single image, unchanged); otherwise render ALL of the
                    report's original attached images (R5 shipped up to 5) —
                    the cross-ring gap this closes, vs the single
                    screenshot_url this surface rendered before. */}
                {f.regression_screenshot_url ? (
                  <SignedScreenshot
                    url={f.regression_screenshot_url}
                    alt="Still-broken screenshot"
                    testId="coord-linked-feedback-screenshot"
                    linkClassName="mt-1 block w-fit"
                  />
                ) : (
                  <ImageGallery
                    images={f.images}
                    legacyUrl={f.screenshot_url}
                    alt="Reporter screenshot"
                    testId="coord-linked-feedback-screenshot"
                    linkClassName="mt-1 block w-fit"
                  />
                )}
                {/* R4 (AC12): the question/answer pair survives promotion --
                    the SAME block the untriaged list renders. */}
                <QuestionAnswerBlock item={f} />
                <div className="mt-1 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="coord-linked-ask-question"
                    onClick={() => onAskQuestion(f)}
                  >
                    {f.operator_question ? 'Edit question' : 'Ask a question'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
